import { useState, useEffect, forwardRef, useImperativeHandle, memo } from "react"
import { TextField, Button, Tooltip, IconButton, Card, CardHeader, CardContent } from "@mui/material"
import DeleteIcon from "@mui/icons-material/Delete"
import { CommonConstant } from "shared/constants/common-constant"
import { rootStore } from "stores/root-store"
import AddIcon from "@mui/icons-material/Add"
import { CustomDataGrid } from "components/common-styles"
import { genId } from "utils/app-helper"
import { GlobalData } from "shared/global-data"
import VirtualizedAutocomplete from "components/common/virtualized-autocomplete"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"

const InboundDeliveryProduct = forwardRef(({ erp_order, curProducts, isSubmitting }, ref) => {
  const columns = [
    {
      field: "line_num",
      headerName: "Line No.",
      width: 100,
      renderCell: params => Number(params.value).toFixed(3),
    },
  ]

  columns.push({
    field: "erp_order",
    headerName: "Purchase Order",
    width: 250,
    renderCell: params => <TextField InputProps={{ readOnly: true }} value={params.value} placeholder={erp_order || "purchase order number"} />,
  })

  columns.push(
    {
      field: "product",
      headerName: "Product",
      minWidth: 250,
      flex: 1,
      description: "product code / item code / SKU code",
      renderCell: params => (
        <VirtualizedAutocomplete
          id={`product${params.row.line_num}`}
          value={params.value}
          isOptionEqualToValue={(option, value) => option.id === value}
          onChange={(event, x) => onPropChanged(params.row["id"], "product", x.id)}
          options={GlobalData.ProductListSelection}
          renderInput={paramsInner => <TextField {...paramsInner} error={Boolean(params.row.validation?.product)} helperText={params.row.validation?.product} />}
        />
      ),
    },
    {
      field: "lot",
      headerName: "Batch",
      width: 200,
      description: "batch / lot number (required for lot controlled items)",
      renderCell: params =>
        rootStore.isLotControlled(params.row.product) ? (
          <TextField
            id={`lot${params.row.line_num}`}
            value={params.value || ""}
            onChange={event => onPropChanged(params.row.id, "lot", event.target.value)}
            error={Boolean(params.row.validation?.lot)}
            helperText={params.row.validation?.lot}
            inputProps={{ maxLength: 25 }}
          />
        ) : null,
    },
    {
      field: "expiration_date_time",
      headerName: "Expiry",
      width: 220,
      description: "expiry date (required for lot controlled items)",
      renderCell: params =>
        rootStore.isLotControlled(params.row.product) ? (
          <DatePicker
            value={params.row.expiration_date_time ? new Date(params.row.expiration_date_time) : null}
            format={CommonConstant.DATE_FORMAT}
            onChange={newValue => onPropChanged(params.row.id, "expiration_date_time", newValue)}
            renderInput={props => (
              <TextField
                {...props}
                id={`expiration_date_time${params.row.line_num}`}
                error={Boolean(params.row.validation?.expiration_date_time)}
                helperText={params.row.validation?.expiration_date_time}
              />
            )}
          />
        ) : null,
    },
    {
      field: "expected_quantity",
      headerName: "Expected Qty",
      width: 200,
      renderCell: params => (
        <TextField
          id={`expected_quantity${params.row.line_num}`}
          value={params.value}
          type="number"
          onChange={event => onPropChanged(params.row.id, "expected_quantity", event.target.value)}
          error={Boolean(params.row.validation.expected_quantity)}
          helperText={params.row.validation.expected_quantity}
          inputProps={{ min: 0 }}
        />
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 80,
      renderCell: params =>
        products.length > 1 && (
          <Tooltip placement="top" title="Delete">
            <IconButton id={`btnDeleteProduct${params.row.line_num}`} aria-label="actions" onClick={() => onDeleteClicked(params.row.line_num)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        ),
    },
  )

  const [products, setProducts] = useState([])

  useEffect(() => {
    if (products.length == 0) {
      setProducts([...products, createProduct()])
    }
  }, [products])

  useEffect(() => {
    const result = [...products]
    result.forEach(p => {
      p.erp_order = erp_order
    })
    setProducts(result)
  }, [erp_order])

  useEffect(() => {
    if (curProducts) {
      const result = [...curProducts]
      result.forEach(p => {
        p.validation = {}
        p.id = genId()
      })
      setProducts(result)
    }
  }, [curProducts])

  useImperativeHandle(ref, () => ({
    getProductsIfValid() {
      if (validateData()) return products

      return null
    },
  }))

  // Uniqueness key combining the product SKU with its lot details.
  // For non-lot-controlled items lot/expiry are empty, so this reduces to SKU alone.
  const getLotKey = item => {
    const lot = (item.lot || "").trim().toUpperCase()
    const expiry = item.expiration_date_time ? new Date(item.expiration_date_time).toDateString() : ""
    return `${lot}__${expiry}`
  }

  const getProductError = (newProducts, item) => {
    const val = item.product
    if (!val) return "Product is required."
    if (!rootStore.isProductAvailable(val)) return `${val.toUpperCase()} is not available.`
    const found = newProducts.find(y => y.id !== item.id && y.action !== "DELETE" && y.product === val && getLotKey(y) === getLotKey(item))
    if (found) return `${val.toUpperCase()} already exists`
    return ""
  }

  const getQuantityError = val => {
    if (!val || parseFloat(val) == 0) return "Expected Qty is required."
    if (parseFloat(val) < 0) return "Invalid number."
    return ""
  }

  // Validate every editable field on a single row, accounting for the
  // product's lot-controlled status (Batch + Expiry are mandatory when Y).
  const validateRow = (newProducts, item) => {
    if (!item.validation) item.validation = {}
    item.validation.product = getProductError(newProducts, item)
    item.validation.expected_quantity = getQuantityError(item.expected_quantity)

    if (rootStore.isLotControlled(item.product)) {
      item.validation.lot = !item.lot ? "Batch is required." : item.lot.length > 25 ? "Batch must be 25 characters or less." : ""
      item.validation.expiration_date_time = item.expiration_date_time ? "" : "Expiry is required."
    } else {
      // Non-lot-controlled items must not carry batch/expiry values.
      item.lot = ""
      item.expiration_date_time = null
      item.validation.lot = ""
      item.validation.expiration_date_time = ""
    }
  }

  const onPropChanged = async (id, prop, val) => {
    let newProducts = [...products]
    const item = newProducts.find(x => x.id === id)
    if (item) {
      if (prop !== "erp_order" && !item["erp_order"]) {
        item["erp_order"] = erp_order
      }

      if (item.line_id) item.action = "CHANGE"

      item[prop] = val
      // Re-validate the whole row: lot/expiry changes affect duplicate detection
      // and changing the product can toggle whether lot details are required.
      validateRow(newProducts, item)

      setProducts(newProducts)
    }
  }

  const onDeleteClicked = line_num => {
    const newProducts = [...products]
    const item = newProducts.find(x => x.line_num === line_num)
    if (item) {
      if (item.line_id) {
        item.action = "DELETE"
        setProducts(newProducts)
      } else {
        setProducts(newProducts.filter(x => x.line_num !== line_num))
      }
    }
  }

  const validateData = () => {
    let isValid = true
    const newProducts = [...products].filter(item => item.action !== "DELETE")
    newProducts.forEach(x => {
      validateRow(newProducts, x)

      if (x.validation.product || x.validation.expected_quantity || x.validation.lot || x.validation.expiration_date_time) isValid = false
    })

    setProducts(newProducts)

    return isValid
  }

  const getLineNumberMaxValue = data => {
    const max = Math.max(...data.map(o => o.line_num), 0)
    return max + 1
  }

  const generateLineNum = () => {
    const realProducts = products.filter(item => item.action !== "DELETE")
    return getLineNumberMaxValue(realProducts)
  }

  const createProduct = () => {
    return {
      action: "NEW",
      product: "",
      erp_order: erp_order,
      expected_quantity: 0,
      lot: "",
      expiration_date_time: null,
      line_id: "",
      line_num: generateLineNum(),
      validation: {},
      id: genId(),
    }
  }

  const handleAddProduct = () => {
    if (validateData()) {
      const newProducts = [...products]
      const product = createProduct()
      setProducts([...newProducts, product])
    }
  }

  return (
    <Card id="cardProducts">
      <CardHeader
        title="Product Information"
        action={
          <Button id="btnAddProduct" color="grey" variant="contained" onClick={handleAddProduct} disabled={isSubmitting} startIcon={<AddIcon />}>
            Add Product
          </Button>
        }
      />
      <CardContent>
        <CustomDataGrid
          getRowId={row => row.id}
          rows={products.filter(x => x.action !== "DELETE")}
          columns={columns}
          initialState={{
            sorting: {
              sortModel: [{ field: "line_num", sort: CommonConstant.ASCENDING }],
            },
            pagination: {
              paginationModel: {
                pageSize: 15,
              },
            },
          }}
          pageSizeOptions={[10, 15, 20, 50]}
          rowHeight={85}
          disableRowSelectionOnClick
        />
      </CardContent>
    </Card>
  )
})

export default memo(InboundDeliveryProduct)
